import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { directedVideoPath } from '../directedVideoPath';
import { priceMessages as messages } from '../messages/priceMessages';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { boldPart } from '../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the price and the make; each part uses its own slice of it */
import styles from './make/Make.module.scss';
import steps from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

/** `DirectedVideoModel.failCode` of a make that failed because too many clips failed. */
const CLIPS_FAILED = 'CLIPS_FAILED';

/**
 * The make failed (design g4): more than a quarter of the shots failed, or the render failed for
 * good. Every credit is back. "Start a new directed video" opens the wizard with no video, which
 * makes a new one (`POST …/directed`, the call of the song page entry). The failed video stays.
 */
const FailedStepComponent: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { accountId, catalogItemUuid, video, leave } = useDirectedVideoContext();
  const { total, failed } = video.clips;
  const credits = video.priceCredits;

  let reason: React.ReactNode;
  if (credits === null) {
    reason = intl.formatMessage(messages.failedNoCredits, { b: bold });
  } else if (video.failCode === CLIPS_FAILED) {
    reason = intl.formatMessage(messages.failedClips, { b: bold, failed, total, credits });
  } else {
    reason = intl.formatMessage(messages.failedRender, { b: bold, credits });
  }

  return (
    <DirectedStepLayout>
      <div className={styles.centre}>
        <div className={`${steps.work} ${styles.failed}`} data-testid="directed-failed">
          <h3>{intl.formatMessage(messages.failedTitle)}</h3>
          <p className={steps.workText}>
            {reason}
            {total - failed > 0 && <> {intl.formatMessage(messages.failedFootage, { count: total - failed })}</>}
          </p>
          <div className={styles.actions}>
            <SMLButton
              variant="primary"
              onClick={() => {
                // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
                void navigate(directedVideoPath(catalogItemUuid, null, { accountId }));
              }}
            >
              {intl.formatMessage(messages.startNew)}
            </SMLButton>
            <SMLButton className={steps.ghost} onClick={leave}>
              {intl.formatMessage(messages.backToSong)}
            </SMLButton>
          </div>
        </div>
      </div>
    </DirectedStepLayout>
  );
};

export const FailedStep = TypedMemo(FailedStepComponent);
